// src/components/buyer-leads/BuyerLeadDrawer.jsx
// Buyer lead detail panel — inline-editable field-by-field (mirrors the Listing
// drawer). Every field saves immediately via updateBuyerLead; the realtime
// listener refreshes `lead`, so there is no draft/Save toggle.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Megaphone, CheckCircle2, Ban, Plus, Trash2, MoreVertical, FileText, Mail, MessageSquare } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import ConvertToDealModal from "./ConvertToDealModal";
import LogActivityModal from "./LogActivityModal";
import AddClientPicker from "../new-contact/AddClientPicker";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import TextThreadModal from "../texting/TextThreadModal";
import LeadConversation from "../leads/LeadConversation";
import LeadPortalForms from "../leads/LeadPortalForms";
import { canUseTexting, maybeSendPortalLinkText } from "../../utils/texting";
import TextNudgeCard from "../texting/TextNudgeCard";
import {
  updateBuyerLead,
  postBuyerNeedToFeed,
  deleteBuyerLead,
  getContactsByIds,
  setContactReferrer,
} from "../../utils/firebasefunctions";
import ReferredByPicker from "../lead-tracker/ReferredByPicker";
import { addContact, updateContact } from "../../utils/contactsApi";
import { normalizeContact } from "../../utils/contactNormalize";
import { listClients, addCoClient, removeClient, clientNames } from "../../utils/leadClients";
import { resolveSystemSource } from "../../utils/leadSourceLabel";
import { emailListingRequestRecipients } from "../../utils/helper";
import { requestBuyerFile } from "../../utils/buyerFileRequest";
import FileRequestDetailsDialog from "../FileRequestDetailsDialog";
import { sendBuyerPortalForms } from "../../utils/sendBuyerPortalForms";
import { resolveLeadRecipients } from "../../utils/portalRecipients";
import PortalRecipientsModal from "../email/PortalRecipientsModal";
import {
  BUYER_STAGE_ORDER,
  TIMELINE_LABELS,
  PRE_APPROVAL_LABELS,
  PROPERTY_TYPE_OPTIONS,
  FINANCING_OPTIONS,
} from "../../utils/buyerLeadStages";
import { RATING_OPTIONS } from "../../utils/buyerActivity";
import { excludeTestAgents } from "../../utils/testAgents";
import { buildTurnInSeed } from "../../utils/buildTurnInSeed";
import EditableNotes from "../reuseable/EditableNotes";
import { useActionDialog } from "../ActionDialog";
import {
  text,
  fmtPrice,
  toNum,
  EditableTextRow,
  EditableSelectRow,
  EditableDateRow,
  EditableChipsRow,
  EditableCsvRow,
} from "../reuseable/InlineEdit";

// Same white panel the listing drawer uses, so both drawers read as one design.
// The grey body behind these is what makes them read as cards.
const Card = ({ label, children, className = "" }) => (
  <div className={`bg-white border border-slate-200 rounded-xl p-4 ${className}`}>
    {label && (
      <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
        {label}
      </h4>
    )}
    {children}
  </div>
);

export default function BuyerLeadDrawer({ lead, canManage, onClose }) {
  const [{ user, leadsource, employees }] = useStateValue();
  const { confirm, prompt, dialog } = useActionDialog();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [sendRecipients, setSendRecipients] = useState(null); // recipient picker
  const [requestDetailsOpen, setRequestDetailsOpen] = useState(false); // file-request questions dialog
  const [menuOpen, setMenuOpen] = useState(false);
  const [coPickerOpen, setCoPickerOpen] = useState(false);
  const [creatingCoContact, setCreatingCoContact] = useState(false);
  const [primaryPickerOpen, setPrimaryPickerOpen] = useState(false);
  const [creatingPrimaryContact, setCreatingPrimaryContact] = useState(false);
  const [primaryContact, setPrimaryContact] = useState(null);
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  // Editing an existing client's contact info right from the drawer:
  // { raw: full contact doc, isPrimary } while the editor is open.
  const [editingClient, setEditingClient] = useState(null);
  const [savingClientEdit, setSavingClientEdit] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!lead?.contactId) {
      setPrimaryContact(null);
      return undefined;
    }
    getContactsByIds([lead.contactId]).then(([raw]) => {
      if (!cancelled) setPrimaryContact(raw ? normalizeContact(raw) : null);
    });
    return () => {
      cancelled = true;
    };
  }, [lead?.contactId]);

  const save = async (patch) => {
    const res = await updateBuyerLead(lead.id, patch);
    if (!res?.success)
      toast.error("Couldn't save that change. Please try again.");
    return res;
  };

  // --- Clients
  const persistClients = (next) =>
    save({ contactId: next.contactId, contactName: next.contactName, coClients: next.coClients || [] });
  const changePrimary = async (contactId) => {
    setPrimaryPickerOpen(false);
    let name = "";
    try { name = normalizeContact((await getContactsByIds([contactId]))[0])?.name || ""; } catch (_) {}
    return save({
      contactId,
      contactName: name,
      coClients: (Array.isArray(lead.coClients) ? lead.coClients : []).filter(
        (c) => c && c.contactId !== contactId,
      ),
    });
  };
  const addCoClientToLead = async (contactId) => {
    setCoPickerOpen(false);
    let name = "";
    try { name = normalizeContact((await getContactsByIds([contactId]))[0])?.name || ""; } catch (_) {}
    await persistClients(addCoClient(lead, { contactId, contactName: name }));
  };
  const removeCoClient = (contactId) => persistClients(removeClient(lead, contactId));
  const handleCreatedCoContact = async (form) => {
    if (!user?.gmailtoken?.access_token) { toast.error("Connect Gmail in Settings before adding a new contact"); return; }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingCoContact(false);
      if (created?.firebaseId) await addCoClientToLead(created.firebaseId);
    } catch (e) { toast.error(e.message || "Failed to create contact"); }
  };
  const handleCreatedPrimaryContact = async (form) => {
    if (!user?.gmailtoken?.access_token) { toast.error("Connect Gmail in Settings before adding a new contact"); return; }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingPrimaryContact(false);
      if (created?.firebaseId) await changePrimary(created.firebaseId);
    } catch (e) { toast.error(e.message || "Failed to create contact"); }
  };

  // Edit an EXISTING client's info without leaving the lead. Reuses the
  // Contacts editor; updateContact syncs to Google when connected and falls
  // back to Firestore-only when not, so no Gmail gate here (unlike create).
  const openEditClient = async (c) => {
    try {
      const [raw] = await getContactsByIds([c.contactId]);
      if (!raw) { toast.error("Couldn't load this contact."); return; }
      setEditingClient({ raw, isPrimary: !!c.primary });
    } catch (e) { toast.error("Couldn't load this contact."); }
  };
  const handleEditedClient = async (form) => {
    if (!editingClient || savingClientEdit) return;
    setSavingClientEdit(true);
    try {
      const { raw, isPrimary } = editingClient;
      await updateContact({ user, contact: raw, form });
      const newName =
        `${form?.firstName || ""} ${form?.lastName || ""}`.trim() ||
        normalizeContact({ ...raw, ...form })?.name ||
        "";
      // Keep the name the lead displays in step with the contact.
      if (isPrimary) {
        await save({ contactName: newName });
        setPrimaryContact(normalizeContact({ ...raw, ...form }));
      } else {
        await save({
          coClients: (Array.isArray(lead.coClients) ? lead.coClients : []).map(
            (cc) =>
              cc && cc.contactId === raw.id
                ? { ...cc, contactName: newName }
                : cc,
          ),
        });
      }
      setEditingClient(null);
      toast.success("Contact updated");
    } catch (e) {
      toast.error(e.message || "Failed to update contact");
    } finally {
      setSavingClientEdit(false);
    }
  };

  // --- Co-agents (inline; mirrors the listing drawer's co-listing agents)
  const co = Array.isArray(lead.coAgents) ? lead.coAgents : [];
  const pool = excludeTestAgents(employees || []);
  const agentOptions = (() => {
    const list = excludeTestAgents(employees || []);
    if (user?.id && !list.some((e) => e.id === user.id)) {
      return [{ id: user.id, firstname: user.firstname, lastname: user.lastname, email: user.email }, ...list];
    }
    return list;
  })().map((a) => ({
    value: a.id,
    label: [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || a.id,
  }));

  const handleOfferAccepted = () => {
    const seed = buildTurnInSeed(
      {
        dealtype: "Buyer",
        customers: listClients(lead).map((c) => c.contactId),
        agentName: lead.assignedAgentName || "",
        agentData: lead.assignedAgent || "",
        leadsource:
          resolveSystemSource(lead.leadSource, leadsource) || lead.leadSource || "",
        // Archive this lead as a won/converted buyer once the deal is created.
        sourceLeadId: lead.id,
        sourceLeadType: "buyer",
      },
      "buyer",
    );
    navigate("/dashboard", { state: { openTurnInSeed: seed } });
  };

  const handlePostToFeed = async () => {
    setBusy(true);
    const res = await postBuyerNeedToFeed(lead, user);
    setBusy(false);
    if (res.success) toast.success("Posted to the Feed");
    else toast.error(res.error || "Failed to post");
  };
  const handleToggleFileRequest = async () => {
    // Turning the request ON with no file yet: ask the file-request questions
    // first (format, deadline, …) so the team gets a complete request. An
    // already-linked file is only being re-flagged — no questions needed.
    if (!lead.fileRequested && !lead.sourceDealId) {
      setRequestDetailsOpen(true);
      return;
    }
    await doToggleFileRequest({});
  };
  const doToggleFileRequest = async (details = {}) => {
    setBusy(true);
    const next = !lead.fileRequested;
    try {
      if (next) {
        // Mint the hidden buyer file (→ Drive folder) and link it to the lead.
        // Idempotent: reuses an existing file if one is already linked.
        await requestBuyerFile(lead, user, { sendForms: false, details });
        setRequestDetailsOpen(false);
        // Email the Settings → Notifications buyer-request recipients. The
        // dialog's answers ride along so the email lists them too.
        emailListingRequestRecipients(
          {
            dealtype: "Buyer",
            customers: listClients(lead)
              .map((c) => c.contactId)
              .filter(Boolean),
            clientLastName: lead.contactName || "",
            agentData: lead.assignedAgent || user?.id || "",
            notes: lead.notes || "",
            createdat: lead.id,
            ...details,
          },
          user,
        ).catch((err) =>
          console.error("Buyer request email blast failed:", err),
        );
        toast.success("File requested");
      } else {
        // Clearing only lowers the flag — it never deletes the file/folder (an
        // uploaded signed agreement must survive).
        const res = await updateBuyerLead(lead.id, { fileRequested: false });
        if (!res.success) throw new Error(res.error || "Failed");
        toast.success("File request cleared");
      }
    } catch (e) {
      console.error("Toggle file request failed:", e);
      toast.error(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };
  // Open the recipient picker (everyone on the file, pre-checked).
  const handleSendBuyerForms = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const recipients = await resolveLeadRecipients(lead);
      setSendRecipients(recipients);
    } catch (e) {
      console.error("Load recipients failed:", e);
      toast.error("Couldn't load the client list — try again");
    } finally {
      setBusy(false);
    }
  };
  // `channel` is "both" | "email" | "text" — the agent's pick in the modal.
  const confirmSendBuyerForms = async (selected, channel = "both") => {
    setBusy(true);
    try {
      const chosen = Array.isArray(selected) ? selected : [];
      const wantsEmail = channel !== "text";
      const wantsText = channel !== "email";
      const recipientEmails = wantsEmail
        ? chosen.map((r) => r?.email).filter(Boolean)
        : [];
      const { deal } = await requestBuyerFile(lead, user, { sendForms: false });
      const sent = await sendBuyerPortalForms(deal, lead, user, { recipientEmails });
      if (sent.ok)
        toast.success(
          sent.demo
            ? "Buyer forms sent (demo)"
            : recipientEmails.length
              ? `Buyer forms sent to ${recipientEmails.length}`
              : "Portal link ready — texting it now",
        );
      else toast.error(`Couldn't send forms: ${sent.reason || "unknown error"}`);
      // Text the link to EACH selected client (only when switched on in
      // Settings; never in demo). Every client the agent picked gets their own
      // text at their own number — not just the primary. The sender resolves
      // each contact's phone and quietly skips anyone without one.
      if (wantsText && sent.ok && !sent.demo && sent.url) {
        chosen
          .filter((r) => r?.contactId)
          .forEach((r) => {
            maybeSendPortalLinkText({
              leadId: lead.id,
              contactId: r.contactId,
              contactName: r.name || "",
              url: sent.url,
            }).catch(() => {});
          });
      }
      setSendRecipients(null);
    } catch (e) {
      console.error("Send buyer forms failed:", e);
      toast.error("Failed to send buyer forms");
    } finally {
      setBusy(false);
    }
  };
  // For buyers whose purchase was ALREADY turned in as a deal separately —
  // archive the lead as won without creating anything. Same archival shape
  // the turn-in flow writes via sourceLeadId.
  const handleAlreadyTurnedIn = async () => {
    const sure = await confirm({
      title: "Already Turned In",
      body: `${lead.contactName || "This buyer"} bought a house and the deal was already turned in? This archives the lead as won — it won't create a new deal.`,
      confirmLabel: "Archive as Won",
    });
    if (!sure) return;
    setBusy(true);
    const res = await updateBuyerLead(lead.id, {
      status: "converted",
      outcome: "won",
      convertedAt: Date.now(),
      archivedBy: "already-turned-in",
    });
    setBusy(false);
    if (res.success) {
      toast.success("Archived as Won");
      onClose();
    } else toast.error("Failed");
  };

  const handleMarkLost = async () => {
    // Cancelling still marks the lead lost, with no reason — the behaviour this
    // has always had. The board's own ⋮ menu bails on cancel instead.
    const reason =
      (await prompt({
        title: "Mark as Lost",
        label: "Reason this lead is lost or inactive (optional)",
        placeholder: "Went with another agent",
        confirmLabel: "Mark as Lost",
      })) || "";
    setBusy(true);
    const res = await updateBuyerLead(lead.id, { status: "lost", lostReason: reason, lostAt: Date.now() });
    setBusy(false);
    if (res.success) { toast.success("Marked as Lost"); onClose(); } else toast.error("Failed");
  };
  const handleDelete = async () => {
    const sure = await confirm({
      title: "Delete Lead",
      body: `Delete ${lead.contactName || "this lead"} from the pipeline?`,
      note: "This removes them from the board entirely.",
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!sure) return;
    setBusy(true);
    const res = await deleteBuyerLead(lead.id);
    setBusy(false);
    if (res.success) { toast.success("Lead deleted"); onClose(); } else toast.error("Failed to delete");
  };

  const timelineOptions = Object.entries(TIMELINE_LABELS).map(([value, label]) => ({ value, label }));

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
          <div className="bg-[#1E293B] text-white px-5 py-4 flex items-center justify-between shrink-0">
            <div>
              <div className="text-lg font-bold flex items-center gap-2">
                {lead.rating && (<span className="text-[11px] font-bold px-1.5 rounded bg-white/20">{lead.rating}</span>)}
                {clientNames(lead) || "Buyer Lead"}
              </div>
              <div className="text-xs text-slate-300">{lead.stage}{lead.status !== "active" ? ` · ${lead.status}` : ""}</div>
            </div>
            <button onClick={onClose} className="p-1 text-slate-300 hover:text-white"><X size={18} /></button>
          </div>


          <TextNudgeCard lead={lead} canManage={canManage} />

          <div className="flex-1 overflow-y-auto bg-slate-50 p-4 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
            {/* LEFT */}
            <div className="flex flex-col gap-4">
              <Card label="Buyer Needs">
                <EditableTextRow k="Price Min" value={lead.priceMin} type="number" display={fmtPrice} canEdit={canManage} placeholder="$450,000" onSave={(v) => save({ priceMin: toNum(v) })} />
                <EditableTextRow k="Price Max" value={lead.priceMax} type="number" display={fmtPrice} canEdit={canManage} placeholder="$525,000" onSave={(v) => save({ priceMax: toNum(v) })} />
                <EditableSelectRow k="Pre-Approval" value={lead.preApproval} canEdit={canManage}
                  options={[{ value: "yes", label: "Pre-Approved" }, { value: "in_progress", label: "In Progress" }, { value: "no", label: "Not Yet" }]}
                  display={(v) => PRE_APPROVAL_LABELS[v] || text(v)} onSave={(v) => save({ preApproval: v })} />
                <EditableTextRow k="Pre-Approval Amount" value={lead.preApprovalAmount} type="number" display={fmtPrice} canEdit={canManage} onSave={(v) => save({ preApprovalAmount: toNum(v) })} />
                <EditableCsvRow k="Areas" value={lead.areas} canEdit={canManage} placeholder="Westside, Downtown" onSave={(areas) => save({ areas })} />
                <EditableChipsRow k="Property Type" value={lead.propertyTypes} canEdit={canManage} options={PROPERTY_TYPE_OPTIONS} onSave={(propertyTypes) => save({ propertyTypes })} />
                <EditableTextRow k="Beds (min)" value={lead.bedsMin} type="number" canEdit={canManage} placeholder="3" onSave={(v) => save({ bedsMin: toNum(v) })} />
                <EditableTextRow k="Baths (min)" value={lead.bathsMin} type="number" canEdit={canManage} placeholder="2" onSave={(v) => save({ bathsMin: toNum(v) })} />
                <EditableSelectRow k="Timeline" value={lead.timeline} canEdit={canManage} options={timelineOptions} display={(v) => TIMELINE_LABELS[v] || text(v)} onSave={(v) => save({ timeline: v })} />
                <EditableSelectRow k="Financing" value={lead.financingType} canEdit={canManage} options={FINANCING_OPTIONS.map((f) => ({ value: f, label: f }))} onSave={(v) => save({ financingType: v })} />
                <EditableTextRow k="Min Sq Ft" value={lead.sqftMin} type="number" canEdit={canManage} placeholder="1400" onSave={(v) => save({ sqftMin: toNum(v) })} />
                <EditableCsvRow k="Must-Haves" value={lead.mustHaves} canEdit={canManage} placeholder="Garage, Yard" onSave={(mustHaves) => save({ mustHaves })} />
                <EditableTextRow k="Motivation" value={lead.motivation} canEdit={canManage} placeholder="Upsizing" onSave={(v) => save({ motivation: v })} />
                <EditableSelectRow k="Must Sell First" value={lead.mustSellFirst} canEdit={canManage} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} onSave={(v) => save({ mustSellFirst: v })} />
                <EditableSelectRow k="Rating" value={lead.rating} canEdit={canManage} options={RATING_OPTIONS.map((r) => ({ value: r, label: r }))} onSave={(v) => save({ rating: v })} />
              </Card>
              <Card label="Pipeline & Follow-Up">
                <EditableSelectRow k="Stage" value={lead.stage} canEdit={canManage} placeholder="New Lead"
                  options={BUYER_STAGE_ORDER.map((s) => ({ value: s, label: s }))} onSave={(v) => save({ stage: v || "New Lead" })} />
                <EditableSelectRow k="Assigned Agent" value={lead.assignedAgent} canEdit={canManage} options={agentOptions}
                  display={() => text(lead.assignedAgentName)}
                  onSave={(v) => {
                    const a = (employees || []).find((e) => e.id === v);
                    const name = a ? [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || "" : "";
                    return save({ assignedAgent: v, assignedAgentName: name });
                  }} />
                {(canManage || co.length > 0) && (
                  <div className="py-2 border-b border-slate-100 text-sm">
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-slate-500 pt-0.5">Co-Agents</span>
                      <div className="flex-1 flex flex-wrap gap-1.5 justify-end">
                        {co.length === 0 ? <span className="text-slate-400">—</span> : co.map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-1 bg-slate-50 text-linkColor rounded-full px-2 py-0.5 text-xs font-medium">
                            {c.name || c.id}
                            {canManage && (
                              <button type="button" className="text-linkColor hover:text-themeColor"
                                onClick={() => save({ coAgents: co.filter((x) => x.id !== c.id) })}>×</button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                    {canManage && (
                      <select className="mt-2 w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700" value=""
                        onChange={(e) => {
                          const id = e.target.value;
                          if (!id) return;
                          const a = pool.find((x) => x.id === id);
                          if (!a || co.some((x) => x.id === id)) return;
                          const name = [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id;
                          save({ coAgents: [...co, { id, name }] });
                        }}>
                        <option value="">+ Add co-agent…</option>
                        {pool.filter((a) => a.id !== lead.assignedAgent && !co.some((c) => c.id === a.id)).map((a) => (
                          <option key={a.id} value={a.id}>{[a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                <EditableDateRow k="Next Follow-Up" value={lead.nextFollowUp} canEdit={canManage} onSave={(v) => save({ nextFollowUp: v })} />
                {/* Opt-in FUB sync: OFF keeps this lead out of Follow Up Boss;
                    flipping ON also pushes it right away (the update's push call
                    passes the backend's fubSync gate once saved). */}
                <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
                  <span className="text-slate-500">Sync to Follow Up Boss</span>
                  <input
                    type="checkbox"
                    checked={lead.fubSync === true}
                    disabled={!canManage}
                    onChange={(e) => save({ fubSync: e.target.checked })}
                  />
                </div>
                <EditableSelectRow k="Lead Source" value={lead.leadSource} canEdit={canManage}
                  options={(leadsource || []).map((s) => ({ value: s.id || s.docId, label: s.name }))}
                  display={(v) => (leadsource || []).find((s) => (s.id || s.docId) === v)?.name || text(v)}
                  onSave={(v) => save({ leadSource: v })} />
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-slate-500">Referred By</span>
                  <span className="flex items-center gap-2 text-sm text-slate-800">
                    {primaryContact?.referredByName || <span className="text-slate-400">None</span>}
                    {canManage && (
                      <button
                        type="button"
                        className="text-xs text-linkColor hover:underline"
                        onClick={() => setRefPickerOpen(true)}
                      >
                        {primaryContact?.referredByName ? "Change" : "Set"}
                      </button>
                    )}
                    {canManage && primaryContact?.referredByName && (
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:underline"
                        onClick={async () => {
                          const r = await setContactReferrer(lead.contactId, null);
                          if (r.success) {
                            setPrimaryContact({ ...primaryContact, referredByContactId: "", referredByName: "" });
                          } else {
                            toast.error("Couldn't clear referrer");
                          }
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </span>
                </div>
                {refPickerOpen && (
                  <ReferredByPicker
                    excludeContactId={lead.contactId}
                    onClose={() => setRefPickerOpen(false)}
                    onDone={async (ref) => {
                      setRefPickerOpen(false);
                      const r = await setContactReferrer(lead.contactId, ref);
                      if (r.success) {
                        setPrimaryContact({
                          ...(primaryContact || {}),
                          referredByContactId: ref.contactId,
                          referredByName: ref.name,
                        });
                      } else {
                        toast.error("Couldn't save referrer");
                      }
                    }}
                  />
                )}
                <EditableCsvRow k="Tags" value={lead.tags} canEdit={canManage} placeholder="first-timer, cash" onSave={(tags) => save({ tags })} />
              </Card>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col gap-4">
              <Card label="Clients">
                {listClients(lead).map((c) => (
                  <div key={c.contactId} className="flex items-center justify-between py-1.5 border-b border-slate-100 text-sm">
                    {/* The name itself opens the contact — same editor the Edit
                       link beside it opens. Falls back to plain text for anyone
                       who can't manage the lead. */}
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => openEditClient(c)}
                        className="text-slate-800 font-medium text-left hover:text-linkColor hover:underline"
                      >
                        {c.contactName || "(unnamed)"}{c.primary ? " · Primary" : ""}
                      </button>
                    ) : (
                      <span className="text-slate-800 font-medium">{c.contactName || "(unnamed)"}{c.primary ? " · Primary" : ""}</span>
                    )}
                    {canManage && (
                      <span className="flex items-center gap-3">
                        <button onClick={() => openEditClient(c)} className="text-xs font-semibold text-sky-600">Edit</button>
                        {c.primary ? (
                          <button onClick={() => setPrimaryPickerOpen(true)} className="text-xs font-semibold text-linkColor">Change</button>
                        ) : (
                          <button onClick={() => removeCoClient(c.contactId)} className="text-xs text-slate-400 hover:text-red-500">Remove</button>
                        )}
                      </span>
                    )}
                  </div>
                ))}
                {canManage && (
                  <button onClick={() => setCoPickerOpen(true)} className="text-sm font-semibold text-sky-600 mt-1">+ Add Co-Client</button>
                )}
              </Card>
              <Card label="Notes">
                {/* hideLabel — the card header already says Notes. */}
                <EditableNotes hideLabel value={lead.notes} canEdit={canManage} onSave={(notes) => save({ notes })} />
              </Card>
              <Card label="Activity">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Client Forms</div>
                {/* Did they actually fill the portal forms out? Reads the file
                    this lead created (lead.sourceDealId). */}
                <LeadPortalForms lead={lead} side="buyer" />
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                {/* Texts + sent emails + logged activity, one timeline. */}
                <LeadConversation lead={lead} />
                </div>
              </Card>
            </div>
          </div>

          {/* Actions live at the bottom, same as the listing drawer. */}
          {canManage && (
            <div className="px-5 py-3 border-t border-slate-200 bg-white flex flex-wrap items-center gap-2 shrink-0">
              {lead.status === "active" && (
                <>
                  <button onClick={handlePostToFeed} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-themeColor text-white rounded-lg px-3 py-2 disabled:opacity-60">
                    <Megaphone size={14} /> {lead.feedPostId ? "Posted ✓ · Re-Post" : "Post Need to Feed"}
                  </button>
                  <button onClick={handleToggleFileRequest} disabled={busy}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 disabled:opacity-60 ${lead.fileRequested ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-slate-700 text-white"}`}>
                    <FileText size={14} /> {lead.fileRequested ? "File Requested ✓" : "Request a File"}
                  </button>
                  <button onClick={handleSendBuyerForms} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2 disabled:opacity-60">
                    <Mail size={14} /> Send Buyer Forms
                  </button>
                  {canUseTexting(user) && (
                    <button onClick={() => setTextOpen(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2">
                      <MessageSquare size={14} /> Text
                    </button>
                  )}
                  <button onClick={() => setLogOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2">
                    <Plus size={14} /> Log Activity
                  </button>
                  {lead.stage === "Offer Submitted" && (
                    <button onClick={handleOfferAccepted}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg px-3 py-2">
                      <CheckCircle2 size={14} /> Offer Accepted → Turn In
                    </button>
                  )}
                </>
              )}
              <div className="relative ml-auto">
                <button onClick={() => setMenuOpen((v) => !v)} aria-label="More Actions"
                  className="inline-flex items-center text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-2 py-2 hover:bg-slate-50">
                  <MoreVertical size={16} />
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                    {/* Opens UPWARD (bottom-full) — the button sits in the
                        drawer's bottom bar, so a downward menu renders off
                        the bottom of the screen. Same as the listing drawer. */}
                    <div className="absolute right-0 bottom-full mb-1 z-20 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-1">
                      {lead.status === "active" && (
                        <button onClick={() => { setMenuOpen(false); handleAlreadyTurnedIn(); }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                          <CheckCircle2 size={14} /> Already Turned In
                        </button>
                      )}
                      {lead.status === "active" && (
                        <button onClick={() => { setMenuOpen(false); handleMarkLost(); }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                          <Ban size={14} /> Mark as Lost
                        </button>
                      )}
                      <button onClick={() => { setMenuOpen(false); handleDelete(); }}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                        <Trash2 size={14} /> Delete Lead
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {coPickerOpen && (
        <AddClientPicker userId={user.id} existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setCoPickerOpen(false)} onPick={addCoClientToLead}
          onCreateNew={() => { setCoPickerOpen(false); setCreatingCoContact(true); }} />
      )}
      {creatingCoContact && (
        <ContactEditDrawer onClose={() => setCreatingCoContact(false)} onSubmit={handleCreatedCoContact} />
      )}
      {primaryPickerOpen && (
        <AddClientPicker userId={user.id} existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setPrimaryPickerOpen(false)} onPick={changePrimary}
          onCreateNew={() => { setPrimaryPickerOpen(false); setCreatingPrimaryContact(true); }} />
      )}
      {creatingPrimaryContact && (
        <ContactEditDrawer onClose={() => setCreatingPrimaryContact(false)} onSubmit={handleCreatedPrimaryContact} />
      )}
      {editingClient && (
        <ContactEditDrawer
          initial={editingClient.raw}
          saving={savingClientEdit}
          onClose={() => setEditingClient(null)}
          onSubmit={handleEditedClient}
        />
      )}
      {convertOpen && (
        <ConvertToDealModal lead={lead} onClose={() => setConvertOpen(false)} onConverted={onClose} />
      )}
      {logOpen && (
        <LogActivityModal lead={lead} onClose={() => setLogOpen(false)} onLogged={() => setLogOpen(false)} />
      )}
      {textOpen && canUseTexting(user) && (
        <TextThreadModal
          leadId={lead.id}
          contactId={lead.contactId}
          contactIds={listClients(lead).map((c) => c.contactId).filter(Boolean)}
          contactName={lead.contactName}
          onClose={() => setTextOpen(false)}
        />
      )}
      {sendRecipients && (
        <PortalRecipientsModal
          title="Send buyer forms"
          recipients={sendRecipients}
          busy={busy}
          onCancel={() => setSendRecipients(null)}
          onSend={confirmSendBuyerForms}
        />
      )}
      {requestDetailsOpen && (
        <FileRequestDetailsDialog
          side="buyer"
          busy={busy}
          onCancel={() => setRequestDetailsOpen(false)}
          onSubmit={doToggleFileRequest}
        />
      )}
      {dialog}
    </>
  );
}
